import styled from "@emotion/styled";
import { useEffect, useRef } from "react";
import { CircularProgress, Typography } from "@mui/material";
import { UserLog } from "./UserLog";

type IUserLog = {
  id: string;
  data: any;
  createdAt: Date;
  action: string;
  userName?: string | undefined;
  createdBy: string;
};

const Container = styled.div`
  position: relative;
  overflow-y: auto;
  width: 100%;

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
`;

const FlexContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 1.5rem;
  margin-top: 3rem;
`;

const UserLogsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: auto;
  gap: 1rem;

  ${(p) => p.theme.media.mobileDown} {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  & > div[data-isartifact="true"] {
    margin-left: 2rem;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${(p) => p.theme.media.mobileDown} {
    height: initial;
  }
`;

const BlockLoadingWrapper = styled(LoadingWrapper)`
  align-items: center;
  width: 100%;
  padding: 10rem 0;
  color: ${(p) => p.theme.text.colors.primary};

  ${(p) => p.theme.media.mobileDown} {
    padding: 6rem 0;
  }
`;

interface Props {
  userLogs: IUserLog[];
  isLoadingUserLogs: boolean;
  createdAtStart: Date | null;
  createdAtFinish: Date | null;
  notFoundMessage?: string;
}

const LAST_MESSAGE_DELAY_MS = 15000;

export const UserLogList = (props: Props) => {
  const {
    userLogs,
    isLoadingUserLogs,
    createdAtStart,
    createdAtFinish,
    notFoundMessage = "This thread has no logs",
  } = props;
  const logsContainerRef = useRef<HTMLDivElement>(null);
  const scrolledRef = useRef<boolean>(false);

  const shouldScroll = createdAtStart && createdAtFinish;

  useEffect(() => {
    if (!shouldScroll || scrolledRef.current === true) return;
    const elements = document.getElementsByClassName("user-log-highlighted");

    const firstElement = elements[0];

    if (!firstElement) return;

    setTimeout(() => {
      const rect = firstElement.getBoundingClientRect();
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      window.scrollTo({
        top: rect.top + scrollTop - 92,
        behavior: "smooth",
      });
      scrolledRef.current = true;
    }, 500);
  }, [userLogs, shouldScroll]);

  if (isLoadingUserLogs) {
    return (
      <FlexContainer>
        <BlockLoadingWrapper>
          <CircularProgress />
        </BlockLoadingWrapper>
      </FlexContainer>
    );
  }

  return (
    <Container ref={logsContainerRef}>
      <UserLogsContainer>
        {!isLoadingUserLogs && userLogs.length === 0 && (
          <Typography textAlign={"center"} variant="body2" mt={2}>
            {notFoundMessage}
          </Typography>
        )}
        {userLogs.length > 0 && (
          <>
            {userLogs.map((userLog) => (
              <UserLog
                key={userLog.id}
                id={userLog.id}
                data={userLog.data}
                userName={userLog.userName}
                action={userLog.action}
                createdBy={userLog.createdBy}
                createdAt={userLog.createdAt}
                highlight={
                  createdAtStart && createdAtFinish
                    ? createdAtFinish.getTime() >=
                        userLog.createdAt.getTime() - LAST_MESSAGE_DELAY_MS &&
                      createdAtStart.getTime() <= userLog.createdAt.getTime()
                    : true
                }
              />
            ))}
            <Typography textAlign={"center"} mt={3}>
              End
            </Typography>
          </>
        )}
      </UserLogsContainer>
    </Container>
  );
};
